import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { ChevronRight, Loader2, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CatalogueProduct, LayerGroup } from "../../catalogue/catalogue";
import {
  filterCatalogue,
  groupByCategory,
  toggleGroup,
  toggleLayer,
  toggleProduct,
  totalSelectedCount,
  totalSelectedCredits,
} from "../../catalogue/catalogue-utils";
import { useClipProducts } from "../../catalogue/use-clip-products";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { Attribute } from "../../models";
import { AttributeTree } from "./attribute-tree";

const CATEGORY_CHIPS = [
  "All",
  "Address",
  "Administrative Boundaries",
  "Statistical Boundaries",
  "Built Environment",
  "Land Parcels",
  "Transport",
] as const;

const CHIP_LABELS: Record<string, string> = {
  All: "All",
  Address: "Address",
  "Administrative Boundaries": "Admin",
  "Statistical Boundaries": "Statistical",
  "Built Environment": "Built Env",
  "Land Parcels": "Land Parcels",
  Transport: "Transport",
};

export const ChooseData = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [selected, setSelected] = useState<Attribute[]>(
    props.clipConfig.attributes ?? [],
  );
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("All");

  const productsQuery = useClipProducts();
  const catalogue = productsQuery.data ?? [];

  useEffect(() => {
    props.setClipConfig((prev) => ({ ...prev, attributes: selected }));
  }, [selected]);

  // Memo'd so we dont recompute on each keystroke
  const grouped = useMemo(
    () => groupByCategory(filterCatalogue(catalogue, search, category)),
    [catalogue, search, category],
  );
  const credits = useMemo(
    () => totalSelectedCredits(catalogue, selected),
    [catalogue, selected],
  );
  const count = useMemo(
    () => totalSelectedCount(catalogue, selected),
    [catalogue, selected],
  );

  const handleToggleProduct = (product: CatalogueProduct, checked: boolean) =>
    setSelected((prev) => toggleProduct(product, prev, checked, catalogue));

  const handleToggleLayer = (
    product: CatalogueProduct,
    configName: string,
    checked: boolean,
  ) => setSelected((prev) => toggleLayer(product, configName, prev, checked));

  const handleToggleGroup = (
    product: CatalogueProduct,
    group: LayerGroup,
    checked: boolean,
  ) => setSelected((prev) => toggleGroup(product, group, prev, checked));

  return (
    <div className="flex flex-col gap-2 h-full">
      <h5 className="text-lg font-medium">Choose your Data</h5>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder="Search products or layers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-1">
        {CATEGORY_CHIPS.map((chip) => (
          <Badge
            key={chip}
            variant={category === chip ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setCategory(chip)}
          >
            {CHIP_LABELS[chip]}
          </Badge>
        ))}
      </div>

      <div className="flex overflow-auto h-full">
        <div className="flex flex-col gap-1 w-full">
          {productsQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="size-4 animate-spin" />
              Loading products…
            </div>
          )}
          <ReactQueryErrorWrapper queries={[productsQuery]} />
          {!productsQuery.isLoading &&
            !productsQuery.isError &&
            grouped.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">
                No products match your search.
              </p>
            )}
          {grouped.map((group) => (
            <Collapsible key={group.category} defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-1 py-1 text-sm font-medium w-full hover:bg-accent/50 rounded px-1 [&[data-state=open]>svg]:rotate-90">
                <ChevronRight className="size-4 transition-transform" />
                {group.category}
                <span className="text-xs text-muted-foreground">
                  ({group.products.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <AttributeTree
                  products={group.products}
                  catalogue={catalogue}
                  selected={selected}
                  search={search}
                  onToggleProduct={handleToggleProduct}
                  onToggleLayer={handleToggleLayer}
                  onToggleGroup={handleToggleGroup}
                />
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground pt-1">
        {count} {count === 1 ? "selection" : "selections"} · {credits} combined
        credits
      </p>

      <div className="self-end mt-auto flex pt-2">
        <Button
          variant="ghost"
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>
        <Button
          id="confirm"
          disabled={selected.length === 0}
          onClick={() => {
            props.setClipConfig((prev) => ({ ...prev, attributes: selected }));
            props.setActiveStep(Math.min(props.activeStep + 1, props.maxSteps));
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
